import React, { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { initializePublicAddressPool, PUBLIC_ADDRESS_POOL_ID } from "../services/publicVerifiedAddresses";
import { readUsedVerifiedAddresses } from "../services/verifiedAddressStorage";
import { migrateLocalAddressUsage, subscribeSharedAddressUsage } from "../services/sharedAddressUsage";
import { getNameForNtid } from "../services/ntidDirectory";
import "./UsedAddresses.css";

const storageError = error => typeof error?.code === "string" && typeof error.message === "string"
  ? error.message : "Shared address history could not be opened. Check your connection and try again.";

export const UsedAddressesPanel = () => {
  const fieldId = useId();
  const [history, setHistory] = useState(null);
  const [zipCode, setZipCode] = useState("");
  const [personSearch, setPersonSearch] = useState("");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    let unsubscribe;
    setHistory(null);
    setError("");
    try {
      unsubscribe = subscribeSharedAddressUsage(entries => {
        if (!active) return;
        setHistory([...entries].sort((left, right) => {
          if (Boolean(left.usedAt) !== Boolean(right.usedAt)) return left.usedAt ? -1 : 1;
          return (right.usedAt || right.recordedAt || "").localeCompare(left.usedAt || left.recordedAt || "")
            || left.address.address.localeCompare(right.address.address);
        }));
        setError("");
      }, failure => {
        if (!active) return;
        setHistory(null);
        setError(storageError(failure));
      });
    } catch (failure) {
      setHistory(null);
      setError(storageError(failure));
    }
    return () => {
      active = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [attempt]);

  const zipCodes = [...new Set((history || []).map(entry => entry.address.zipCode))].sort();
  const searchPerson = personSearch.trim().replace(/\s+/g, " ").toLowerCase();
  // Resolve names at display time so existing immutable usage records benefit too.
  const filtered = (history || []).filter(entry => (!zipCode || entry.address.zipCode === zipCode)
    && (!searchPerson || `${entry.ntid} ${getNameForNtid(entry.ntid)}`.toLowerCase().includes(searchPerson)));

  return <section className="used-addresses-panel" aria-labelledby={`${fieldId}-title`}>
    <header className="used-addresses-heading">
      <div><h1 id={`${fieldId}-title`}>Used Addresses</h1><p>Shared address usage for your team. See the name, NTID and used date for each address.</p></div>
      <Link className="used-addresses-back" to="/get-address">Get Address</Link>
    </header>
    <div className="used-addresses-controls">
      <div className="used-addresses-field">
        <label htmlFor={`${fieldId}-zip`}>ZIP code</label>
        <select id={`${fieldId}-zip`} value={zipCode} onChange={event => setZipCode(event.target.value)}>
          <option value="">All ZIP codes</option>
          {zipCodes.map(zip => <option key={zip} value={zip}>{zip}</option>)}
        </select>
      </div>
      <div className="used-addresses-field used-addresses-ntid">
        <label htmlFor={`${fieldId}-ntid`}>Name or NTID</label>
        <input id={`${fieldId}-ntid`} type="search" value={personSearch} maxLength={100} placeholder="Search name or NTID"
          autoComplete="off" spellCheck="false" onChange={event => setPersonSearch(event.target.value)} />
      </div>
      {history && <p className="used-addresses-count" role="status">
        <strong>{filtered.length}</strong>{zipCode || searchPerson ? ` of ${history.length}` : ""} used {history.length === 1 ? "address" : "addresses"}
      </p>}
    </div>
    {error && <div className="used-addresses-error" role="alert"><p>{error}</p><button className="used-addresses-back" type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div>}
    {!history && !error && <p className="used-addresses-message" role="status">Loading shared address history…</p>}
    {history && history.length === 0 && <p className="used-addresses-empty">No addresses have been used by your team yet. <Link to="/get-address">Get an address</Link> to start a usage record.</p>}
    {history && history.length > 0 && filtered.length === 0 && <p className="used-addresses-empty">No used addresses match these filters.</p>}
    {filtered.length > 0 && <div className="used-addresses-table-scroll" role="region" aria-label="Used address history" tabIndex={0}>
      <table className="used-addresses-table">
        <caption>Shared used addresses for your team</caption>
        <thead><tr><th scope="col">Address</th><th scope="col">ZIP</th><th scope="col">Name</th><th scope="col">NTID</th><th scope="col">Used on</th><th scope="col">Provider</th></tr></thead>
        <tbody>{filtered.map(entry => <tr key={entry.address.id}>
          <td className="used-addresses-address">{entry.address.address}</td>
          <td>{entry.address.zipCode}</td>
          <td className="used-addresses-name">{getNameForNtid(entry.ntid) || <span className="used-addresses-unrecorded">{entry.ntid ? "Not listed" : "Not recorded"}</span>}</td>
          <td>{entry.ntid || <span className="used-addresses-unrecorded">Not recorded</span>}</td>
          <td>{entry.usedAt ? <time dateTime={entry.usedAt} title={entry.usedAt}>{new Date(entry.usedAt).toLocaleString()}</time>
            : <span className="used-addresses-unrecorded">Not recorded</span>}</td>
          <td>{entry.address.provider === "T-Mobile" ? "T-Mobile 5G" : entry.address.provider}</td>
        </tr>)}</tbody>
      </table>
    </div>}
    <p className="used-addresses-footnote">No sign-in is needed. This history is shared with your team across devices. Older records may have no NTID or used date.</p>
  </section>;
};

const UsedAddresses = () => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError("");
    const prepare = async () => {
      try {
        initializePublicAddressPool();
        await migrateLocalAddressUsage(readUsedVerifiedAddresses(PUBLIC_ADDRESS_POOL_ID));
        if (active) setReady(true);
      } catch (failure) { if (active) setError(storageError(failure)); }
    };
    prepare();
    return () => { active = false; };
  }, [attempt]);
  return <main className="used-addresses-page">
    {ready ? <UsedAddressesPanel /> : <section className="used-addresses-panel" aria-labelledby="used-addresses-title">
      <h1 id="used-addresses-title">Used Addresses</h1>
      {error ? <div className="used-addresses-error" role="alert"><p>{error}</p><button className="used-addresses-back" type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div>
        : <p className="used-addresses-message" role="status">Loading shared address history…</p>}
      <Link className="used-addresses-back" to="/get-address">Get Address</Link>
    </section>}
  </main>;
};

export default UsedAddresses;
